import {
    CARRYOVER_ROOT,
    EXTERNAL_DOCS_ROOT_PATH,
    MARVIN_EVAL_RUNS_ROOT_PATH,
    MARVIN_SETTINGS_ROOT_PATH,
    MIRANDA_RUNS_ROOT_PATH,
    PRECON_ONTOLOGY_ROOT_PATH,
    REVISION_DASH_ROOT_PATH,
    ENGAGEMENT_REPORTS_PATH,
    USAGE_ROOT_PATH,
} from "./constants";

/**
 * Who sees a page's entry. The client only hides; each page's route and API check again.
 * - "admin": the current tenant's admins (`useIsAdmin`).
 * - "documentAdmin": tenant admins and project admins (`PrivateWrapper requireDocumentAdmin`).
 * - "staff": Primepoint staff (ROLE_PRIMEPOINT_ADMIN), as the ops routes' require_role.
 * - "mirandaViewer", "ontologyViewer": primepoint_admin or the staff registry's stytch_admin,
 *   as those routers admit; "marvinEvalViewer": primepoint_admin only.
 * - "tenancy": company-level "manage_projects" authority, or staff.
 */
export type AdminAudience =
    | "admin"
    | "documentAdmin"
    | "staff"
    | "mirandaViewer"
    | "marvinEvalViewer"
    | "ontologyViewer"
    | "tenancy";

/**
 * Where a page is listed:
 * - "console": the Admin console itself, the one entry staff have in the Admin menu;
 * - "accounts" … "platform": the console's side nav, the fleet's pages;
 * - "project": one project's admin pages, listed in the console under "Last project";
 * - "projectSettings": a section of Project settings, listed there and under "Last project";
 * - "deepLink": opened from another page, never listed.
 */
export type AdminGroup =
    | "console"
    | "accounts"
    | "operations"
    | "engagement"
    | "marvin"
    | "platform"
    | "project"
    | "projectSettings"
    | "deepLink";

export interface AdminPage {
    key: string;
    label: string;
    /** The page's address, and the root of every address under it. */
    path: string;
    /** Where its entry opens when that is not `path` (Marvin settings opens its Settings tab). */
    to?: string;
    audience: AdminAudience;
    group: AdminGroup;
    /**
     * Its route contract (`routeContract`): "project" runs under the tab's project, "global"
     * without one. Absent for the console's areas, which `/admin/console` declares. A Project
     * settings section declares its own, so switching project keeps the section.
     */
    scope?: "project" | "global";
    /** A global page that runs without a project (`RouteContract.projectFree`). */
    projectFree?: true;
    /** Where switching project from it lands, when not its own address. */
    switchTo?: string;
    /** Its address before it moved: a project route that redirects here. */
    movedFrom?: string;
    /** Its name in the console's side nav, which has its own Companies and Projects. */
    consoleLabel?: string;
    /** Rarely opened: fewer than 10 prod page views in the 30 days to 2026-10-30 (Amplitude). The
     *  console lists it under its collapsed More instead of its group; nothing else changes. */
    rare?: true;
}

const CONSOLE_ROOT = "/admin/console";

/** A console area: routed inside the console, which declares their contract. */
const consolePage = <K extends string>(key: K, label: string, group: AdminGroup) =>
    ({ key, label, path: `${CONSOLE_ROOT}/${key}`, audience: "staff", group }) as const;

/** One project's admin page, for the tenant's admins. */
const projectPage = <K extends string>(key: K, label: string, path: string) =>
    ({ key, label, path, audience: "admin", group: "project", scope: "project" }) as const;

/** A Project settings section that was an Admin menu page of its own at `movedFrom`. */
const settingsSection = <K extends string>(
    key: K,
    label: string,
    segment: string,
    movedFrom: string,
    audience: AdminAudience = "admin",
) =>
    ({
        key,
        label,
        path: `/settings/project/${segment}`,
        movedFrom,
        audience,
        group: "projectSettings",
        scope: "project",
    }) as const;

const PAGES = [
    {
        key: "console",
        label: "Admin console",
        path: CONSOLE_ROOT,
        audience: "staff",
        group: "console",
        scope: "global",
        projectFree: true,
    },

    // The console's areas and the fleet's pages, in side-nav order. Fleet and staff pages name
    // the tenants they read in the request, but the header, Feedback, Marvin and the exclusion
    // review act on the tab's project: without a project in the URL they acted on the browser
    // session's organization instead of the project the person chose.
    consolePage("companies", "Companies", "accounts"),
    consolePage("projects", "Projects", "accounts"),
    consolePage("people", "People", "accounts"),
    consolePage("staff", "Staff", "accounts"),
    // Acts on many projects, each through its own route with the tenant in the URL
    // (`staffProjectApi`, /ops/revision-dash), which a page holding one project's admission refuses.
    {
        key: "dataStatus",
        label: "Project data status",
        path: REVISION_DASH_ROOT_PATH,
        audience: "staff",
        group: "operations",
        scope: "global",
        projectFree: true,
    },
    consolePage("activity", "Activity", "operations"),
    consolePage("pipeline", "Pipeline", "operations"),
    // Usage (engagement across every project): a page of its own outside the console, opened from
    // the app bar's Staff menu beside the Admin console, listed here so the console and the route
    // contract know it. It reads every
    // tenant's numbers through `/ops/usage/*`, so it runs without a project.
    {
        key: "usage",
        label: "Usage",
        path: USAGE_ROOT_PATH,
        audience: "staff",
        group: "operations",
        scope: "global",
        projectFree: true,
    },
    // The Engagement row (Overview, Projects, Scoreboard, Uses, Ask): day-by-day views beside Usage's weeks
    // and months; listed only while its global flag is on (ConsoleNav).
    consolePage("engagement", "Engagement", "engagement"),
    // The per-project and fleet engagement reports (`/events/report`, `/events/fleet-report`), beside Usage.
    {
        key: "engagementReports",
        label: "Engagement reports",
        path: ENGAGEMENT_REPORTS_PATH,
        audience: "staff",
        group: "engagement",
        scope: "project",
    },
    {
        key: "marvinSettings",
        label: "Marvin settings",
        path: MARVIN_SETTINGS_ROOT_PATH,
        to: `${MARVIN_SETTINGS_ROOT_PATH}/settings`,
        audience: "staff",
        group: "marvin",
        scope: "project",
        rare: true,
    },
    {
        key: "marvinEvalRuns",
        label: "Marvin eval runs",
        path: MARVIN_EVAL_RUNS_ROOT_PATH,
        audience: "marvinEvalViewer",
        group: "marvin",
        scope: "project",
        rare: true,
    },
    {
        key: "mirandaRuns",
        label: "Miranda runs",
        path: MIRANDA_RUNS_ROOT_PATH,
        audience: "mirandaViewer",
        group: "marvin",
        scope: "project",
    },
    {
        key: "ontology",
        label: "Ontology",
        path: PRECON_ONTOLOGY_ROOT_PATH,
        audience: "ontologyViewer",
        group: "marvin",
        scope: "project",
        rare: true,
    },
    {
        key: "globalFlags",
        label: "Global feature flags",
        path: "/admin/flags/global",
        audience: "staff",
        group: "platform",
        scope: "project",
        rare: true,
    },
    consolePage("emails", "Emails", "platform"),
    {
        key: "exclusions",
        label: "Exclusion review",
        path: "/admin/connection-exclusions",
        audience: "staff",
        group: "platform",
        scope: "project",
        rare: true,
    },
    // Company and project tenancy, answered without a project.
    {
        key: "tenancy",
        label: "Companies & projects",
        consoleLabel: "Advanced",
        path: "/admin/tenancy",
        audience: "tenancy",
        group: "platform",
        scope: "global",
        projectFree: true,
    },

    // One project's admin pages.
    projectPage("overview", "Overview", "/admin"),
    projectPage("revisions", "Revision processing", "/admin/revisions"),
    projectPage("externalDocs", "Externally managed docs", EXTERNAL_DOCS_ROOT_PATH),
    projectPage("users", "User management", "/admin/users"),
    projectPage("flags", "Feature flags", "/admin/flags"),
    projectPage("jobs", "Jobs", "/admin/jobs"),
    { ...projectPage("processing", "Drawing processing", "/admin/processing"), rare: true },
    { ...projectPage("llmUsage", "LLM Usage", "/admin/llm-usage"), rare: true },
    projectPage("feedback", "Feedback", "/admin/feedback"),
    // An inspect tool for what the transfer did to a sheet, not a project surface. The entry is
    // ungated because the tool answers for itself: a project whose transfers were recorded by a
    // path that writes nothing opens on a screen saying exactly that.
    { ...projectPage("carryover", "Component transfer diff", CARRYOVER_ROOT), rare: true },
    { ...projectPage("taxonomy", "Taxonomy console", "/admin/taxonomy"), rare: true },
    { ...projectPage("validation", "Validation", "/admin/validation"), rare: true },
    { ...projectPage("projectHealth", "Project health", "/admin/project-health"), rare: true },
    {
        ...projectPage("connectionHealth", "Connection health", "/admin/connection-health"),
        rare: true,
    },
    {
        ...projectPage("revisionQuality", "Revision quality", "/admin/revision-quality"),
        rare: true,
    },

    // Project settings' admin sections: settings of the project, beside its others.
    settingsSection(
        "disciplines",
        "Disciplines",
        "disciplines",
        "/admin/disciplines",
        "documentAdmin",
    ),
    {
        ...settingsSection(
            "destinations",
            "Destinations",
            "destinations",
            "/admin/destination-visibility",
        ),
        rare: true,
    },
    { ...settingsSection("gridlines", "Grid lines", "gridlines", "/admin/gridlines"), rare: true },
    settingsSection("announcement", "Announcement", "announcement", "/admin/announcement"),
    settingsSection("emailTeam", "Email team", "email", "/admin/emailer"),

    // Opened from another page, never listed.
    {
        ...projectPage("streamEvents", "Stream events", "/admin/stream-events"),
        group: "deepLink",
        switchTo: "/admin/jobs",
    },
    { ...projectPage("parseDebug", "Parse debug", "/admin/parse-debug"), group: "deepLink" },
    // The verifier's old address, which opens External docs' Browser.
    {
        ...projectPage("externalFiles", "External files", "/admin/external-files"),
        group: "deepLink",
    },
] as const satisfies readonly AdminPage[];

export type AdminPageKey = (typeof PAGES)[number]["key"];

/**
 * Every admin page, once: the Admin menu, the console's side nav, Project settings' admin
 * sections and the route contract all read this list.
 */
export const ADMIN_PAGES: readonly AdminPage[] = PAGES;

export function adminPage(key: AdminPageKey): AdminPage {
    return ADMIN_PAGES.find((page) => page.key === key)!;
}

/** Where a page's entry opens. */
export const adminPageHref = (page: AdminPage): string => page.to ?? page.path;

/**
 * The app bar's Admin menu's pages. Staff get the Staff menu: the Admin console, whose side nav
 * lists every admin page, and Usage. Everyone else sees each page their audience admits that is not a console page or
 * a deep link, by label: a tenant admin the project's pages and its Project settings admin
 * sections, a company admin Companies & projects, a staff registry admin the Miranda runs and
 * the Ontology.
 */
export function adminMenuPages(
    audiences: Readonly<Record<AdminAudience, boolean>>,
    isStaff: boolean,
): AdminPage[] {
    if (isStaff) return [adminPage("console"), adminPage("usage")];
    return ADMIN_PAGES.filter(
        (page) => page.group !== "console" && page.group !== "deepLink" && audiences[page.audience],
    ).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/** The pages listed in the console's side nav, under each group's label, in order. */
export const CONSOLE_GROUPS: readonly { group: AdminGroup; label: string }[] = [
    { group: "accounts", label: "Accounts" },
    { group: "operations", label: "Operations" },
    { group: "engagement", label: "Engagement" },
    { group: "marvin", label: "Marvin & Miranda" },
    { group: "platform", label: "Platform" },
    // Bare project addresses: they open in the project this browser opened last, or ask for one.
    { group: "project", label: "Last project" },
    { group: "projectSettings", label: "Project settings" },
];
